import React, { useState, useEffect } from 'react'
import Sidebar, { NAV_ITEMS, NavIcon } from '../components/Sidebar'
import PrivateAviation  from './PrivateAviation'
import PartnerHotels    from './PartnerHotels'
import CarTransfers     from './CarTransfers'
import AirportServices  from './AirportServices'
import MemberProfile    from './MemberProfile'
import AboutUs          from './AboutUs'
import Blog             from './Blog'
import RequestHistory   from './RequestHistory'

const VIDEO_WEBM = '/images/video1.mp4'
const VIDEO_MP4  = '/images/video1.mp4'

const MOCK_REQUESTS = [
  { id: 1, service: 'Private Aviation', detail: 'BRU → DXB',                     date: '14 Apr 2026', status: 'In Progress' },
  { id: 2, service: 'Car Transfer',     detail: 'Hotel Amigo → Brussels Airport', date: '10 Apr 2026', status: 'Completed'   },
  { id: 3, service: 'Partner Hotels',   detail: 'Dubai — Atlantis The Royal',     date: '02 Apr 2026', status: 'Confirmed'   },
]

const MOCK_MEMBER = {
  name:        'H.E. Ambassador Leila Al-Rashidi',
  initials:    'LA',
  memberId:    'MBR-00142',
  email:       'l.alrashidi@embassy.example.com',
  phone:       '+32 2 xxx xx xx',
  country:     'United Arab Emirates',
  institution: 'Embassy',
  jobTitle:    'Ambassador',
  position:    'Head of Mission',
  represents:  'United Arab Emirates',
  joinDate:    'March 2024',
}

/* Saved companions — in production comes from member profile API */
const MOCK_COMPANIONS = [
  { name: 'Marie Al-Rashidi',  initials: 'MA', role: 'Spouse',    passport: 'AE-123456', nationality: 'United Arab Emirates' },
  { name: 'Fatima Al-Rashidi', initials: 'FA', role: 'Assistant', passport: 'AE-654321', nationality: 'United Arab Emirates' },
]

const getGreeting = () => {
  const h = new Date().getHours()
  if (h < 12) return 'Good morning'
  if (h < 18) return 'Good afternoon'
  return 'Good evening'
}

const getFirstName = (fullName) => {
  const skip = ['H.E.', 'H.R.H.', 'Dr.', 'Prof.', 'Mr.', 'Ms.', 'Mrs.', 'Sir', 'Lord', 'Ambassador']
  const parts = fullName.split(' ')
  return parts.filter(p => !skip.includes(p))[0] || parts[0]
}

/* Pages that receive standard props */
const ALL_PAGES = {
  hotels:    PartnerHotels,
  transfers: CarTransfers,
  airport:   AirportServices,
  profile:   MemberProfile,
  about:     AboutUs,
  blog:      Blog,
  history:   RequestHistory,
}

export default function Dashboard({ onSignOut }) {
  const [activeSection, setActiveSection] = useState('home')
  const [collapsed,     setCollapsed]     = useState(false)
  const [isMobile,      setIsMobile]      = useState(false)
  const [drawerOpen,    setDrawerOpen]    = useState(false)
  const [entered,       setEntered]       = useState(false)
  const [lang,          setLang]          = useState('en')

  useEffect(() => {
    const t = setTimeout(() => setEntered(true), 60)
    return () => clearTimeout(t)
  }, [])

  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 768)
    check()
    window.addEventListener('resize', check)
    return () => window.removeEventListener('resize', check)
  }, [])

  const navigate = (id) => {
    setActiveSection(id)
    if (isMobile) setDrawerOpen(false)
  }

  const ActivePage = ALL_PAGES[activeSection] || null

  return (
    <div className={`db-shell${entered ? ' db-shell--in' : ''}`}>

      {/* VIDEO BACKGROUND */}
      <div className="db-bg" aria-hidden="true">
        <video className="db-bg__video" autoPlay muted loop playsInline preload="auto">
          <source src={VIDEO_WEBM} type="video/webm" />
          <source src={VIDEO_MP4}  type="video/mp4"  />
        </video>
        <div className="db-bg__overlay" />
        <div className="db-bg__grain"   />
      </div>

      {/* Mobile backdrop */}
      {isMobile && drawerOpen && (
        <div className="db-backdrop" onClick={() => setDrawerOpen(false)} aria-hidden="true" />
      )}

      {/* SIDEBAR */}
      <Sidebar
        activeSection={activeSection}
        collapsed={collapsed}
        isMobile={isMobile}
        drawerOpen={drawerOpen}
        onNavigate={navigate}
        onCollapse={() => setCollapsed(c => !c)}
        onSignOut={onSignOut}
        member={MOCK_MEMBER}
        lang={lang}
        onLangChange={setLang}
      />

      {/* MAIN */}
      <div className="db-main">

        {/* Mobile topbar */}
        {isMobile && (
          <div className="db-topbar">
            <button className="db-topbar__burger" onClick={() => setDrawerOpen(d => !d)} aria-label="Open menu">
              <span /><span /><span />
            </button>
            <img src="/images/logo.png" alt="Mwuïlu" />
            <div style={{ width: 36 }} />
          </div>
        )}

        {/* HOME */}
        {activeSection === 'home' && (
          <div className="db-page db-home">

            <header className="db-home__greeting">
              <div className="db-home__greeting-left">
                <p className="db-home__salutation">
                  {getGreeting()},&nbsp;<em>{getFirstName(MOCK_MEMBER.name)}</em>
                </p>
                <h1 className="db-home__headline">
                  How may we attend<br />to you today?
                </h1>
              </div>
              <div className="db-home__greeting-right">
                <p className="db-urgent-link">
                  Urgent? One Call
                </p>
              </div>
            </header>

            <section aria-label="Services">
              <p className="db-overline">Our Services</p>
              <div className="db-tiles">
                <ServiceTile icon={<IconAviation />}  label="Flight Arrangements"  desc="Business and first class only."    onClick={() => navigate('aviation')}  />
                <ServiceTile icon={<IconHotels />}    label="Partner Hotels"        desc="Preferred 4★ and 5★ hotels only." onClick={() => navigate('hotels')}    />
                <ServiceTile icon={<IconTransfers />} label="Car Transfers"         desc="Private ground transportation."    onClick={() => navigate('transfers')} />
                <ServiceTile icon={<IconAirport />}   label="Airport Coordination"  desc="Meet & greet, escort & transfer." onClick={() => navigate('airport')}   />
              </div>
            </section>

            <section aria-label="Recent requests">
              <div className="db-section-header-row">
                <p className="db-overline">Recent Requests</p>
                <button className="db-view-all" onClick={() => navigate('history')}>
                  View all →
                </button>
              </div>
              <div className="db-requests">
                {MOCK_REQUESTS.map((req, i) => (
                  <div key={req.id} className="db-request" style={{ animationDelay: `${i * 60}ms` }}>
                    <div className="db-request__left">
                      <span className="db-request__service">{req.service}</span>
                      <span className="db-request__detail">{req.detail}</span>
                    </div>
                    <div className="db-request__right">
                      <span className="db-request__date">{req.date}</span>
                      <StatusBadge status={req.status} />
                    </div>
                  </div>
                ))}
              </div>
            </section>

          </div>
        )}

        {/* PRIVATE AVIATION — gets companions passed in */}
        {activeSection === 'aviation' && (
          <PrivateAviation
            onBack={() => navigate('home')}
            member={MOCK_MEMBER}
            savedCompanions={MOCK_COMPANIONS}
          />
        )}

        {/* ALL OTHER PAGES */}
        {ActivePage && (
          <ActivePage
            onBack={() => navigate('home')}
            onNavigate={navigate}
            member={MOCK_MEMBER}
          />
        )}

      </div>

      {/* MOBILE BOTTOM NAV */}
      {isMobile && (
        <nav className="db-bottom-nav" aria-label="Bottom navigation">
          <button
            className={`db-bottom-nav__item${activeSection === 'home' ? ' db-bottom-nav__item--active' : ''}`}
            onClick={() => navigate('home')}
          >
            <IconHomeSmall />
            <span>Home</span>
          </button>
          {NAV_ITEMS.slice(0, 3).map(item => (
            <button
              key={item.id}
              className={`db-bottom-nav__item${activeSection === item.id ? ' db-bottom-nav__item--active' : ''}`}
              onClick={() => navigate(item.id)}
            >
              <NavIcon id={item.id} />
              <span>{item.label.split(' ').pop()}</span>
            </button>
          ))}
          <button className="db-bottom-nav__item" onClick={() => setDrawerOpen(true)}>
            <IconMore />
            <span>More</span>
          </button>
        </nav>
      )}

    </div>
  )
}

/* ── Sub-components ── */
function ServiceTile({ icon, label, desc, onClick }) {
  return (
    <button className="db-tile" onClick={onClick}>
      <div className="db-tile__icon">{icon}</div>
      <span className="db-tile__label">{label}</span>
      <span className="db-tile__desc">{desc}</span>
      <span className="db-tile__caret" aria-hidden="true">→</span>
    </button>
  )
}

function StatusBadge({ status }) {
  const map = {
    'In Progress': 'db-badge--progress',
    'Confirmed':   'db-badge--confirmed',
    'Completed':   'db-badge--completed',
    'Received':    'db-badge--received',
  }
  return <span className={`db-badge ${map[status] ?? ''}`}>{status}</span>
}

const SV = { fill: 'none', stroke: 'currentColor', strokeWidth: '0.9', strokeLinecap: 'round', strokeLinejoin: 'round' }
function IconHomeSmall() { return <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M2 7L8 2L14 7V14H10V10H6V14H2V7Z" {...SV} /></svg> }
function IconMore()      { return <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><circle cx="8" cy="8" r="1" fill="currentColor" /><circle cx="3" cy="8" r="1" fill="currentColor" /><circle cx="13" cy="8" r="1" fill="currentColor" /></svg> }
function IconAviation()  { return <svg width="26" height="26" viewBox="0 0 26 26" fill="none"><path d="M22 7.5L16 13H22L18.5 20.5H15.5L13 14.5L3.5 19V16.5L11 12.5L3.5 5V2.5L13 6.5L15.5 1H18.5L22 7.5Z" {...SV} /></svg> }
function IconHotels()    { return <svg width="26" height="26" viewBox="0 0 26 26" fill="none"><rect x="3" y="10" width="20" height="13" rx="0.5" {...SV} /><path d="M7.5 23V18H10.5V23" {...SV} /><rect x="14" y="17" width="5" height="4" {...SV} /><path d="M3 10L13 3L23 10" {...SV} /></svg> }
function IconTransfers() { return <svg width="26" height="26" viewBox="0 0 26 26" fill="none"><rect x="2" y="10" width="22" height="8" rx="2" {...SV} /><path d="M5 10L7.5 5H18.5L21 10" {...SV} /><circle cx="7" cy="20" r="2" {...SV} /><circle cx="19" cy="20" r="2" {...SV} /></svg> }
function IconAirport()   { return <svg width="26" height="26" viewBox="0 0 26 26" fill="none"><path d="M4 22H22" {...SV} /><path d="M13 5V17" {...SV} /><path d="M8.5 12.5L13 17L17.5 12.5" {...SV} /><circle cx="13" cy="5" r="1.8" {...SV} /></svg> }